import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resources } from '@/mocks/resources';

interface SearchBarProps {
  size?: 'md' | 'lg';
  defaultValue?: string;
  placeholder?: string;
}

export default function SearchBar({
  size = 'md',
  defaultValue = '',
  placeholder = '搜索国语影音资源、导演或类型…',
}: SearchBarProps) {
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const keyword = value.trim();
  const suggestions = keyword
    ? resources
        .filter(
          (item) =>
            item.title.includes(keyword) ||
            item.director.includes(keyword) ||
            item.genres.some((genre) => genre.includes(keyword)) ||
            item.region.includes(keyword)
        )
        .slice(0, 6)
    : [];

  const doSearch = (kw: string) => {
    const target = kw.trim();
    if (!target) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(target)}`);
  };

  const isLarge = size === 'lg';

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          doSearch(value);
        }}
        className={`flex w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 pl-4 pr-1.5 ${
          isLarge ? 'h-14' : 'h-10'
        }`}
      >
        <i
          className={`ri-search-line text-foreground-500 ${
            isLarge ? 'text-xl' : 'text-base'
          }`}
        />
        <input
          type="text"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className={`min-w-0 flex-1 bg-transparent text-foreground-950 placeholder:text-foreground-500 focus:outline-none ${
            isLarge ? 'text-base' : 'text-sm'
          }`}
        />
        <button
          type="submit"
          className={`shrink-0 rounded-full bg-primary-500 font-label text-background-50 transition-colors hover:bg-primary-600 ${
            isLarge ? 'h-11 px-6 text-sm' : 'h-7 px-4 text-xs'
          }`}
        >
          搜索
        </button>
      </form>

      {open && keyword && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2">
          {suggestions.length > 0 ? (
            suggestions.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => doSearch(item.title)}
                className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-background-100"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-movie-2-line" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground-950">
                    {item.title}
                  </span>
                  <span className="block truncate text-xs text-foreground-600">
                    {item.year} · {item.region} · {item.genres.join(' / ')}
                  </span>
                </span>
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-foreground-600">
              未找到“{keyword}”相关内容，试试其他关键词
            </div>
          )}
        </div>
      )}
    </div>
  );
}